import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Plus, Trash2, Pencil, Building2, MapPin, Mail, Star, X, Archive, RotateCcw } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { OrphanSitesCard } from '../components/OrphanSitesCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SitesPage = () => {
  const { user } = useAuth();
  const [sites, setSites] = useState([]);
  const [orphans, setOrphans] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [restoringOrphan, setRestoringOrphan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingSite, setEditingSite] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    description: '',
    client_email: '', // Campo legacy per retrocompatibilità
    client_name: '',
    emails: [] // Nuovo sistema: [{address: string, is_primary: boolean}]
  });

  useEffect(() => {
    fetchSites();
    fetchOrphans();
  }, []);

  const fetchSites = async () => {
    try {
      const response = await axios.get(`${API}/sites/all`);
      // Sort sites alphabetically by name
      const sorted = response.data.sort((a, b) => {
        return (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase(), 'it');
      });
      setSites(sorted);
    } catch (error) {
      toast.error('Errore nel caricamento dei cantieri');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrphans = async () => {
    try {
      const response = await axios.get(`${API}/sites/orphans`);
      setOrphans(response.data);
    } catch (error) {
      setOrphans([]);
    }
  };

  const activeSites = sites.filter(s => s.active !== false);
  const archivedSites = sites.filter(s => s.active === false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Prepara i dati da inviare
      const dataToSend = {
        name: formData.name,
        address: formData.address,
        description: formData.description,
        client_name: formData.client_name,
        emails: formData.emails.filter(e => e.address.trim() !== ''),
        // Imposta client_email dall'email primaria per retrocompatibilità
        client_email: formData.emails.find(e => e.is_primary)?.address || formData.emails[0]?.address || ''
      };

      if (restoringOrphan) {
        await axios.post(`${API}/sites/restore`, { ...dataToSend, site_id: restoringOrphan.site_id });
        toast.success(`Cantiere ripristinato! ${restoringOrphan.report_count} rapporti ricollegati.`);
        fetchOrphans();
      } else if (editingSite) {
        await axios.put(`${API}/sites/${editingSite.id}`, dataToSend);
        toast.success('Cantiere aggiornato!');
      } else {
        await axios.post(`${API}/sites`, dataToSend);
        toast.success('Cantiere creato!');
      }
      setIsDialogOpen(false);
      setEditingSite(null);
      setRestoringOrphan(null);
      resetForm();
      fetchSites();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    }
  };

  const handleRestoreOrphan = (orphan) => {
    setEditingSite(null);
    setRestoringOrphan(orphan);
    setFormData({
      name: orphan.suggested_name || '',
      address: '',
      description: '',
      client_email: orphan.suggested_client_email || '',
      client_name: '',
      emails: orphan.suggested_client_email ? [{ address: orphan.suggested_client_email, is_primary: true }] : []
    });
    setIsDialogOpen(true);
  };

  const handleReactivate = async (site) => {
    try {
      await axios.post(`${API}/sites/${site.id}/reactivate`);
      toast.success(`Cantiere "${site.name}" riattivato!`);
      fetchSites();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella riattivazione');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      address: '',
      description: '',
      client_email: '',
      client_name: '',
      emails: []
    });
  };

  const handleEdit = (site) => {
    setEditingSite(site);
    // Migra dal campo legacy se emails è vuoto
    let emails = site.emails || [];
    if (emails.length === 0 && site.client_email) {
      emails = [{ address: site.client_email, is_primary: true }];
    }
    setFormData({
      name: site.name,
      address: site.address,
      description: site.description || '',
      client_email: site.client_email || '',
      client_name: site.client_name || '',
      emails: emails
    });
    setIsDialogOpen(true);
  };

  // Gestione email multiple
  const addEmail = () => {
    const newEmails = [...formData.emails, { address: '', is_primary: formData.emails.length === 0 }];
    setFormData({ ...formData, emails: newEmails });
  };

  const removeEmail = (index) => {
    const newEmails = formData.emails.filter((_, i) => i !== index);
    // Se rimuoviamo l'email primaria, imposta la prima come primaria
    if (newEmails.length > 0 && !newEmails.some(e => e.is_primary)) {
      newEmails[0].is_primary = true;
    }
    setFormData({ ...formData, emails: newEmails });
  };

  const updateEmailAddress = (index, address) => {
    const newEmails = [...formData.emails];
    newEmails[index] = { ...newEmails[index], address };
    setFormData({ ...formData, emails: newEmails });
  };

  const setPrimaryEmail = (index) => {
    const newEmails = formData.emails.map((email, i) => ({
      ...email,
      is_primary: i === index
    }));
    setFormData({ ...formData, emails: newEmails });
  };

  const handleDelete = async (site) => {
    if (!window.confirm(`Sei sicuro di voler eliminare il cantiere "${site.name}"?\n\nSe ha rapporti collegati verrà archiviato (non più selezionabile per nuovi rapporti, ma visibile nei filtri e nei rapporti esistenti).`)) return;
    try {
      const response = await axios.delete(`${API}/sites/${site.id}`);
      if (response.data?.archived) {
        toast.info(response.data.message, { duration: 6000 });
      } else {
        toast.success('Cantiere eliminato!');
      }
      fetchSites();
      fetchOrphans();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1B6B93]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="sites-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">Cantieri</h1>
          <p className="text-slate-500 mt-1">
            {activeSites.length} cantieri attivi{archivedSites.length > 0 && ` · ${archivedSites.length} archiviati`}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {archivedSites.length > 0 && (
            <Button
              variant={showArchived ? 'secondary' : 'outline'}
              onClick={() => setShowArchived(!showArchived)}
              data-testid="toggle-archived-btn"
            >
              <Archive className="w-4 h-4 mr-2" />
              {showArchived ? 'Nascondi archiviati' : `Archiviati (${archivedSites.length})`}
            </Button>
          )}
          <Dialog open={isDialogOpen} onOpenChange={(open) => {
            setIsDialogOpen(open);
            if (!open) {
              setEditingSite(null);
              setRestoringOrphan(null);
              resetForm();
            }
          }}>
            <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="new-site-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nuovo Cantiere
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-md bg-white">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-[#1B6B93]" data-testid="site-dialog-title">
                {restoringOrphan ? 'Ripristina Cantiere' : editingSite ? 'Modifica Cantiere' : 'Nuovo Cantiere'}
              </DialogTitle>
            </DialogHeader>
            {restoringOrphan && (
              <div className="bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-sm text-amber-800" data-testid="restore-info">
                Verrà ricreato con lo stesso identificativo: i <strong>{restoringOrphan.report_count}</strong> rapporti collegati torneranno associati a questo cantiere. Controlla nome e indirizzo.
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Nome Cantiere</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Uffici Via Roma"
                  required
                  className="input-field bg-white"
                  data-testid="site-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Indirizzo</Label>
                <Input
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="Via Roma 123, 80100 Napoli"
                  required
                  className="input-field bg-white"
                  data-testid="site-address-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Descrizione (opzionale)</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Note sul cantiere..."
                  className="input-field bg-white min-h-[80px]"
                  data-testid="site-description-input"
                />
              </div>

              {/* Sezione Email Multiple */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Email Cliente</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addEmail}
                    className="h-7 text-xs"
                    data-testid="add-email-btn"
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Aggiungi Email
                  </Button>
                </div>
                
                {formData.emails.length === 0 ? (
                  <p className="text-xs text-slate-400 italic">
                    Nessuna email configurata. Clicca "Aggiungi Email" per iniziare.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {formData.emails.map((email, index) => (
                      <div key={index} className="flex items-center gap-2" data-testid={`email-row-${index}`}>
                        <div className="flex-1 relative">
                          <Input
                            type="email"
                            value={email.address}
                            onChange={(e) => updateEmailAddress(index, e.target.value)}
                            placeholder="cliente@email.com"
                            className={`input-field bg-white pr-8 ${email.is_primary ? 'border-amber-400' : ''}`}
                            data-testid={`email-input-${index}`}
                          />
                          {email.is_primary && (
                            <Star className="w-4 h-4 absolute right-2 top-1/2 -translate-y-1/2 text-amber-500 fill-amber-500" />
                          )}
                        </div>
                        <Button
                          type="button"
                          variant={email.is_primary ? "secondary" : "ghost"}
                          size="sm"
                          onClick={() => setPrimaryEmail(index)}
                          className={`h-9 px-2 ${email.is_primary ? 'bg-amber-100 text-amber-700' : ''}`}
                          title={email.is_primary ? "Email principale (riceve approvazioni)" : "Imposta come principale"}
                          data-testid={`set-primary-btn-${index}`}
                        >
                          <Star className={`w-4 h-4 ${email.is_primary ? 'fill-amber-500 text-amber-500' : 'text-slate-400'}`} />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeEmail(index)}
                          className="h-9 px-2 text-red-500 hover:bg-red-50"
                          data-testid={`remove-email-btn-${index}`}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                    <div className="bg-slate-50 p-2 rounded text-xs text-slate-600 mt-2">
                      <div className="flex items-center gap-1 mb-1">
                        <Star className="w-3 h-3 text-amber-500 fill-amber-500" />
                        <span className="font-medium">Email principale:</span>
                      </div>
                      <span>Riceve la richiesta di approvazione e il PDF finale firmato</span>
                      <br />
                      <span className="text-slate-500">Le altre email ricevono solo il PDF finale</span>
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label>Nome Cliente (per PDF)</Label>
                <Input
                  value={formData.client_name}
                  onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                  placeholder="Azienda XYZ S.r.l."
                  className="input-field bg-white"
                  data-testid="site-client-name-input"
                />
                <p className="text-xs text-slate-500">
                  Verrà usato nel nome del file PDF
                </p>
              </div>
              
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Annulla
                </Button>
                <Button type="submit" className="btn-primary" data-testid="site-submit-btn">
                  {restoringOrphan ? 'Ripristina' : editingSite ? 'Aggiorna' : 'Crea'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <OrphanSitesCard orphans={orphans} onRestore={handleRestoreOrphan} />

      {/* Sites Grid */}
      {activeSites.length === 0 && !showArchived ? (
        <Card className="shadow-sm">
          <CardContent className="text-center py-12">
            <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">Nessun cantiere registrato</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 stagger-children">
          {(showArchived ? [...activeSites, ...archivedSites] : activeSites).map((site) => {
            const isArchived = site.active === false;
            return (
            <Card key={site.id} className={`shadow-sm hover:shadow-md transition-shadow animate-fade-in ${isArchived ? 'opacity-70 border-dashed' : ''}`} data-testid={`site-card-${site.id}`}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${isArchived ? 'bg-slate-200' : 'bg-[#A8D8EA]'}`}>
                      <Building2 className={`w-6 h-6 ${isArchived ? 'text-slate-500' : 'text-[#1B6B93]'}`} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-800">{site.name}</h3>
                      {isArchived && (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-slate-600 bg-slate-100 rounded-full px-2 py-0.5 mt-1" data-testid={`archived-badge-${site.id}`}>
                          <Archive className="w-3 h-3" /> Archiviato
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="mt-4 flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-slate-600">{site.address}</p>
                </div>
                
                {/* Visualizzazione email multiple */}
                {(site.emails && site.emails.length > 0) ? (
                  <div className="mt-2 space-y-1">
                    {site.emails.map((email, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-slate-400 flex-shrink-0" />
                        <p className="text-sm text-slate-600">{email.address}</p>
                        {email.is_primary && (
                          <Star className="w-3 h-3 text-amber-500 fill-amber-500 flex-shrink-0" />
                        )}
                      </div>
                    ))}
                  </div>
                ) : site.client_email ? (
                  <div className="mt-2 flex items-center gap-2">
                    <Mail className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    <p className="text-sm text-slate-600">{site.client_email}</p>
                  </div>
                ) : null}
                
                {site.description && (
                  <p className="mt-3 text-sm text-slate-500 line-clamp-2">
                    {site.description}
                  </p>
                )}
                
                <div className="mt-4 pt-4 border-t border-slate-100 flex justify-end gap-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleEdit(site)}
                    data-testid={`edit-site-btn-${site.id}`}
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  {isArchived ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-emerald-700 hover:bg-emerald-50"
                      onClick={() => handleReactivate(site)}
                      data-testid={`reactivate-site-btn-${site.id}`}
                    >
                      <RotateCcw className="w-4 h-4 mr-1" /> Riattiva
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-600 hover:bg-red-100"
                      onClick={() => handleDelete(site)}
                      data-testid={`delete-site-btn-${site.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SitesPage;
